import { IAccount } from '@medely/types';
import { MedelyDateTime } from '@medely/date-time-tools';
import moment from 'moment';

export interface ValidationResult {
  canContinue: boolean;
  message: string;
}

type MinimalWorkHistory = {
  position_id?: number | null;
  position?: { id?: number | null } | null;
  start_date?: string | null;
  end_date?: string | null;
  currently_work_here: boolean;
};

const ALLIED_REQUIRED_DAYS = 180;
const NON_ALLIED_REQUIRED_DAYS = 240;

/**
 * Validates if the user has sufficient experience based on work history or current input.
 *
 * @param workHistories - List of the user's past work experiences
 * @param currentUser - The current user object containing profile info
 * @param options - Optional: start/end date and current employment for inline form validation
 * @returns Whether the user can continue and a possible error message
 */
export const validateExperience = (
  workHistories: MinimalWorkHistory[],
  currentUser: IAccount | undefined,
  options?: {
    startDate?: Date;
    endDate?: Date;
    currentlyWorkHere?: boolean;
  },
): ValidationResult => {
  const { isAllied, isNonAllied, requiredMonths, hasResume, totalYearsOfExperience } =
    getProMetadata(currentUser);

  if (hasResume) {
    return success();
  }

  if (options?.startDate && (options?.currentlyWorkHere || options?.endDate)) {
    const parsedStart = MedelyDateTime.fromMoment(moment(options.startDate), { tz: 'system' });
    const parsedEnd = MedelyDateTime.fromMoment(moment(options.endDate ?? new Date()), {
      tz: 'system',
    });

    const isEndAfterStart = parsedEnd.isAfter(parsedStart);
    const isEndNotFuture = parsedEnd.isBefore(
      MedelyDateTime.fromMoment(moment(), { tz: 'system' }),
    );
    const monthsDiff = Math.abs(parsedStart.difference(parsedEnd, 'months'));

    if (!isEndAfterStart || !isEndNotFuture) {
      return failureWithMessage('Invalid date range. Please check the start and end date.');
    }

    if (monthsDiff < requiredMonths) {
      return failure(isAllied, requiredMonths);
    }

    const allowableYears = Math.max(totalYearsOfExperience / 2, 1.5);
    const cutoffDate = MedelyDateTime.fromMoment(moment(), { tz: 'system' }).minus({
      years: allowableYears,
    });

    const isTooOld = parsedEnd.isBefore(cutoffDate);

    if (isTooOld) {
      return failureWithYears(isAllied, requiredMonths, allowableYears);
    }

    return success();
  }

  const grouped = groupWorkHistoriesByPosition(workHistories);

  const hasEnoughAllied = Object.values(grouped).some(
    (histories) => getNonOverlappingDays(histories) >= ALLIED_REQUIRED_DAYS,
  );

  const totalNonAllied = getNonOverlappingDays(workHistories);

  const passesAlliedCheck = isAllied && hasEnoughAllied;
  const passesNonAlliedCheck = isNonAllied && totalNonAllied >= NON_ALLIED_REQUIRED_DAYS;

  if (passesAlliedCheck || passesNonAlliedCheck) {
    return success();
  }

  return failure(isAllied, requiredMonths);
};

// ====== HELPERS ======

/**
 * Extracts metadata needed for validation from the current user
 */
function getProMetadata(currentUser?: IAccount) {
  const kinds = currentUser?.professional?.kinds ?? [];
  const resume = currentUser?.professional?.resume;
  const positions = currentUser?.professional?.professional_positions ?? [];

  const isAllied = kinds.includes('allied');
  const isNonAllied = kinds.some((k) => k !== 'allied');

  const requiredDays = isAllied ? ALLIED_REQUIRED_DAYS : NON_ALLIED_REQUIRED_DAYS;
  const requiredMonths = requiredDays / 30;
  const totalYearsOfExperience = positions.reduce(
    (acc, pos) => acc + (pos.years_of_experience ?? 0),
    0,
  );

  return {
    isAllied,
    isNonAllied,
    requiredMonths,
    hasResume: !!resume,
    totalYearsOfExperience,
  };
}

/**
 * Returns a generic success result
 */
function success(): ValidationResult {
  return { canContinue: true, message: '' };
}

/**
 * Returns a default failure result based on the kind of user
 */
function failure(isAllied: boolean, months: number): ValidationResult {
  const message = isAllied
    ? `You must have a resume or minimum ${months} months experience in at least one position to continue.`
    : `You must have a resume or minimum ${months} months experience added to continue.`;
  return { canContinue: false, message };
}

/**
 * Returns a failure message with year-based expiration info
 */
function failureWithYears(isAllied: boolean, months: number, years: number): ValidationResult {
  const formattedYears = Math.round(years * 10) / 10;
  const plural = formattedYears === 1 ? 'year' : 'years';
  const message = isAllied
    ? `You must have a resume or minimum ${months} months experience in one position within the last ${formattedYears} ${plural}.`
    : `You must have a resume or minimum ${months} months experience added within the last ${formattedYears} ${plural}.`;
  return { canContinue: false, message };
}

/**
 * Returns a simple custom message
 */
function failureWithMessage(msg: string): ValidationResult {
  return { canContinue: false, message: msg };
}

/**
 * Groups work history items by their position ID
 */
function groupWorkHistoriesByPosition(workHistories: MinimalWorkHistory[]) {
  return workHistories.reduce<Record<number, MinimalWorkHistory[]>>((acc, h) => {
    const posId = h.position_id ?? h.position?.id;
    if (!posId) {
      return acc;
    }
    acc[posId] = acc[posId] || [];
    acc[posId].push(h);
    return acc;
  }, {});
}

/**
 * Calculates non-overlapping days across a list of work histories
 */
function getNonOverlappingDays(histories: MinimalWorkHistory[]): number {
  if (!histories.length) {
    return 0;
  }

  const sorted = histories
    .filter((h) => h.start_date)
    .map((h) => ({
      start: new Date(h.start_date!),
      end: h.currently_work_here ? new Date() : new Date(h.end_date || new Date()),
    }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const merged: { start: Date; end: Date }[] = [sorted[0]];

  for (const curr of sorted.slice(1)) {
    const last = merged[merged.length - 1];
    if (curr.start <= last.end) {
      last.end = new Date(Math.max(last.end.getTime(), curr.end.getTime()));
    } else {
      merged.push(curr);
    }
  }

  return merged.reduce((total, { start, end }) => total + diffInDays(start, end), 0);
}

/**
 * Calculates the number of days between two dates
 */
function diffInDays(start: Date, end: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil((end.getTime() - start.getTime()) / msPerDay);
}
